/**
 * InlineEditText - Click-to-edit single-line text
 *
 * Enter commits, Escape cancels, blur commits. Empty input commits `null`, so it doubles as a
 * "clear the value" control for optional fields like list notes.
 */
import clsx from 'clsx';
import type { KeyboardEvent, ReactElement } from 'react';
import { useRef, useState } from 'react';

export interface InlineEditTextProps {
  value: string | null;
  onCommit: (value: string | null) => void;
  /** Shown in read mode when the value is empty. */
  placeholder?: string;
  ariaLabel: string;
  maxLength?: number;
  disabled?: boolean;
  className?: string;
}

export default function InlineEditText({
  value,
  onCommit,
  placeholder = 'Add…',
  ariaLabel,
  maxLength = 500,
  disabled = false,
  className,
}: InlineEditTextProps): ReactElement {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState('');
  // Escape must suppress the blur that immediately follows it, or blur would re-commit the draft.
  const isCancellingRef = useRef(false);

  const startEdit = () => {
    if (disabled) return;
    setDraft(value ?? '');
    isCancellingRef.current = false;
    setIsEditing(true);
  };

  const commit = () => {
    if (isCancellingRef.current) return;
    setIsEditing(false);
    const trimmed = draft.trim();
    const next = trimmed.length > 0 ? trimmed : null;
    if (next !== value) onCommit(next);
  };

  const cancel = () => {
    isCancellingRef.current = true;
    setIsEditing(false);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancel();
    }
  };

  if (isEditing) {
    return (
      <input
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={commit}
        maxLength={maxLength}
        autoFocus
        aria-label={ariaLabel}
        className={clsx(
          'focus:border-aether w-full min-w-0 rounded border border-zinc-600 bg-zinc-800 px-2 py-1 text-sm text-zinc-200 focus:outline-none',
          className,
        )}
      />
    );
  }

  return (
    <button
      type="button"
      onClick={startEdit}
      disabled={disabled}
      aria-label={ariaLabel}
      className={clsx(
        'focus-visible:ring-aether w-full min-w-0 truncate rounded px-2 py-1 text-left text-sm transition-colors',
        'hover:bg-zinc-800/60 focus:outline-none focus-visible:ring-2',
        'disabled:cursor-not-allowed disabled:hover:bg-transparent',
        value ? 'text-zinc-300' : 'text-zinc-600 italic',
        className,
      )}
    >
      {value || placeholder}
    </button>
  );
}
